import { useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import useSWR, { mutate } from "swr";
import { JSON_FETCHER } from "@/components/fetcher";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/page-header";
import { Copy, Users, Gift, BookOpen, DollarSign, Clock, Banknote } from "lucide-react";
import Link from "next/link";

export default function ReferralPage() {
  const [copied, setCopied] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [isSubmittingPayout, setIsSubmittingPayout] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [payoutSuccess, setPayoutSuccess] = useState<string | null>(null);

  const {
    data: referralData,
    error: codeError,
    isLoading: codeLoading,
  } = useSWR("/web_api/referral/code", JSON_FETCHER);

  const {
    data: stats,
    error: statsError,
    isLoading: statsLoading,
  } = useSWR("/web_api/referral/stats", JSON_FETCHER);

  const {
    data: commissionData,
    error: commissionError,
    isLoading: commissionLoading,
  } = useSWR("/web_api/referral/commissions/summary", JSON_FETCHER);

  const {
    data: payoutEligibility,
    error: eligibilityError,
    isLoading: eligibilityLoading,
  } = useSWR("/web_api/referral/payout/eligibility", JSON_FETCHER);

  const {
    data: payoutHistory,
    error: historyError,
    isLoading: historyLoading,
  } = useSWR("/web_api/referral/payout/history", JSON_FETCHER);

  const copyToClipboard = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePayoutRequest = async () => {
    if (!paypalEmail || !payoutEligibility?.available_amount) return;

    setIsSubmittingPayout(true);
    setPayoutError(null);
    setPayoutSuccess(null);

    try {
      const response = await fetch("/web_api/referral/payout/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paypal_email: paypalEmail,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPayoutSuccess(
          "Payout request submitted successfully! We'll review it and process payment within 5-7 business days.",
        );
        setPaypalEmail("");
        // Refresh data
        mutate("/web_api/referral/payout/eligibility");
        mutate("/web_api/referral/commissions/summary");
        mutate("/web_api/referral/payout/history");
      } else {
        setPayoutError(data.error || "Failed to submit payout request");
      }
    } catch (error) {
      setPayoutError("Network error. Please try again.");
    } finally {
      setIsSubmittingPayout(false);
    }
  };

  if (codeLoading || statsLoading || commissionLoading || eligibilityLoading || historyLoading) {
    return (
      <DashboardLayout
        activePath="/dashboard/referral"
        title="Referral Program"
        breadcrumbs={[{ label: "Referral Program" }]}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (codeError || statsError || commissionError || eligibilityError || historyError) {
    return (
      <DashboardLayout
        activePath="/dashboard/referral"
        title="Referral Program"
        breadcrumbs={[{ label: "Referral Program" }]}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error loading referral data</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      activePath="/dashboard/referral"
      title="Referral Program"
      breadcrumbs={[{ label: "Referral Program" }]}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Referral Program"
          description="Share Uptime Monitor and earn commission rewards"
        />

        {/* Referral Link Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              Your Referral Link
            </CardTitle>
            <CardDescription>
              Share this link with friends to invite them to Uptime Monitor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={referralData?.referral_link || ""}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
              />
              <Button onClick={copyToClipboard} variant="default">
                <Copy className="h-4 w-4 mr-2" />
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Referral Code:{" "}
              <span className="font-mono font-semibold">{referralData?.referral_code}</span>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Referral Statistics
            </CardTitle>
            <CardDescription>Track your referrals and commission earnings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center py-6 md:border-r border-gray-200 dark:border-gray-700">
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                  {stats?.total_referred || 0}
                </div>
                <div className="text-gray-600 dark:text-gray-400 mt-2">Users Referred</div>
              </div>
              <div className="text-center py-6 md:border-r border-gray-200 dark:border-gray-700">
                <div className="text-4xl font-bold text-purple-600 dark:text-purple-400">
                  ${((commissionData?.total_lifetime_earnings || 0) / 100).toFixed(2)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 mt-2">Total Earned</div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  All-time earnings
                </div>
              </div>
              <div className="text-center py-6 md:border-r border-gray-200 dark:border-gray-700">
                <div className="text-4xl font-bold text-orange-600 dark:text-orange-400">
                  ${((commissionData?.pending_earnings || 0) / 100).toFixed(2)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 mt-2">Pending Earnings</div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Recent earnings in 30-day hold period
                </div>
              </div>
              <div className="text-center py-6">
                <div className="text-4xl font-bold text-green-600 dark:text-green-400">
                  ${((commissionData?.net_earnings || 0) / 100).toFixed(2)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 mt-2">Available for Payout</div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Ready to withdraw
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payout Request Card - Hide if pending/approved payout exists */}
        {!(() => {
          const hasPendingOrApprovedPayout = payoutHistory?.history?.some(
            (payout: any) => payout.status === "pending" || payout.status === "approved",
          );
          return hasPendingOrApprovedPayout;
        })() && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Request Payout
              </CardTitle>
              <CardDescription>
                Request a payout of your available earnings via PayPal
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payoutEligibility?.is_eligible ? (
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                      <DollarSign className="h-5 w-5" />
                      <span className="font-semibold">
                        ${(payoutEligibility.available_amount / 100).toFixed(2)} available for
                        payout
                      </span>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Minimum payout: ${(payoutEligibility.min_payout_amount / 100).toFixed(2)}
                    </p>
                  </div>

                  {payoutSuccess && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <p className="text-blue-700 dark:text-blue-300">{payoutSuccess}</p>
                    </div>
                  )}

                  {payoutError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                      <p className="text-red-700 dark:text-red-300">{payoutError}</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="paypal-email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        PayPal Email Address
                      </label>
                      <input
                        id="paypal-email"
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        placeholder="your-paypal@example.com"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        disabled={isSubmittingPayout}
                      />
                    </div>

                    <Button
                      onClick={handlePayoutRequest}
                      disabled={!paypalEmail || isSubmittingPayout}
                      className="w-full cursor-pointer"
                    >
                      {isSubmittingPayout
                        ? "Submitting..."
                        : `Request Payout ($${(payoutEligibility.available_amount / 100).toFixed(2)})`}
                    </Button>
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <p>• Payouts are processed within 5-7 business days</p>
                    <p>• You can only have one pending payout request at a time</p>
                    <p>• PayPal processing fees may apply</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                    <Clock className="h-5 w-5" />
                    <span className="font-semibold">Payout not available</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {payoutEligibility?.error_message || "Requirements not met for payout"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payout History Card */}
        {payoutHistory?.history && payoutHistory.history.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Payout History
              </CardTitle>
              <CardDescription>Your recent payout requests and their status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payoutHistory.history.map((payout: any) => {
                  const getStatusColor = (status: string) => {
                    switch (status) {
                      case "pending":
                        return "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700";
                      case "approved":
                        return "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700";
                      case "paid":
                        return "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700";
                      case "rejected":
                        return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700";
                      default:
                        return "text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700";
                    }
                  };

                  const getStatusText = (status: string) => {
                    switch (status) {
                      case "pending":
                        return "Under Review";
                      case "approved":
                        return "Approved - Processing";
                      case "paid":
                        return "Paid";
                      case "rejected":
                        return "Rejected";
                      default:
                        return status.charAt(0).toUpperCase() + status.slice(1);
                    }
                  };

                  return (
                    <div
                      key={payout.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            ${(payout.amount / 100).toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            To: {payout.paypal_email}
                          </div>
                        </div>
                        <div
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(payout.status)}`}
                        >
                          {getStatusText(payout.status)}
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                        <span>Requested: {new Date(payout.requested_at).toLocaleDateString()}</span>
                        {payout.processed_at && (
                          <span>
                            Processed: {new Date(payout.processed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {payout.notes && (
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2">
                          <strong>Note:</strong> {payout.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Marketing Call-to-Action Block */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700">
          <CardContent className="p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Earn 20% Lifetime Commission
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                Share Uptime Monitor with your network and earn recurring commission on every
                subscription payment. Start earning today - no approval needed!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>Get your referral link above and start sharing</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span>Earn $4.99/month per $24.95 customer</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documentation Link */}
        <div className="mt-6 text-center">
          <Link
            href="/docs/referral"
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <BookOpen className="h-4 w-4" />
            Learn more about the referral program
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
